import Compressor from "compressorjs"
import CryptoJS from "crypto-js"
import { clamp } from "lodash"
import { UPLOAD_ACCEPTED_FILE_TYPES } from "~/data/const"

export interface ImageDimensions {
  width: number
  height: number
}

export class Files {
  public static async generateKey(file: File) {
    const fileContent = await Files.readAsArrayBuffer(file)
    const md5 = CryptoJS.MD5(CryptoJS.lib.WordArray.create(fileContent))

    return md5
  }

  public static async readAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.addEventListener("load", () => {
        resolve(reader.result as string)
      })
      reader.addEventListener("error", () => {
        reject(`Unable to read file ${file.name}`)
      })
      reader.readAsText(file)
    })
  }

  public static async readAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.addEventListener("load", () => {
        resolve(reader.result as ArrayBuffer)
      })
      reader.addEventListener("error", () => {
        reject(`Unable to read file ${file.name}`)
      })
      reader.readAsArrayBuffer(file)
    })
  }

  public static async getImageDimensions(file: File): Promise<ImageDimensions> {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader()
      fileReader.onload = () => {
        const image = new Image()
        image.onload = () => {
          resolve({
            height: image.height,
            width: image.width,
          })
        }
        image.onerror = error => {
          reject(error)
        }

        image.src = fileReader.result as string
      }

      fileReader.readAsDataURL(file)
    })
  }

  public static async resizeImage(file: File): Promise<File> {
    const maxWidthFromScreen = clamp(window.screen.width * 0.8, 1000, 1920)
    return new Promise((resolve, reject) => {
      // @see https://github.com/fengyuanchen/compressorjs
      // for more options
      new Compressor(file, {
        quality: 0.8,
        maxWidth: maxWidthFromScreen,
        maxHeight: 1000,
        checkOrientation: false,
        success(result: any) {
          resolve(result as File)
        },
        error(err) {
          reject(err)
        },
      })
    })
  }

  public static async compressImage(file: File, rate: number): Promise<File> {
    return new Promise((resolve, reject) => {
      // @see https://github.com/fengyuanchen/compressorjs
      // for more options
      new Compressor(file, {
        quality: rate,
        checkOrientation: true,
        success(result: any) {
          resolve(result as File)
        },
        error(err) {
          reject(err)
        },
      })
    })
  }

  public static async convertHeicToJpeg(file: File): Promise<File> {
    // dynamically import the heic-convert library
    // to avoid loading it in the main bundle
    const module = await import("heic-convert/browser")
    const convert = module.default

    const buffer = await file.arrayBuffer()

    const convertedImage = await convert({
      buffer: new Uint8Array(buffer),
      format: "JPEG",
    })

    const newBlob = new Blob([convertedImage], {
      type: "image/jpeg",
    })

    const newFile = new File([newBlob], file.name.replace(".heic", ".jpeg"), {
      type: "image/jpeg",
    })

    return newFile
  }

  public static async promptForImageUpload(): Promise<File[]> {
    const allAcceptedExtensions = UPLOAD_ACCEPTED_FILE_TYPES.filter(t =>
      t.includes("/")
    ).map(type => `.${type.split("/")[1]}`)

    const acceptAttr = Array.from(
      new Set([...UPLOAD_ACCEPTED_FILE_TYPES, ...allAcceptedExtensions])
    ).join(",")

    return await new Promise<File[]>(resolve => {
      const input = document.createElement("input")
      input.type = "file"
      input.multiple = true
      input.accept = acceptAttr
      input.style.position = "fixed"
      input.style.left = "-9999px" // keep in DOM for iOS Safari

      const cleanup = () => {
        // Defer to avoid interfering with the file chooser on some mobiles
        setTimeout(() => {
          input.remove()
          window.removeEventListener("focus", onWindowFocus, true)
        }, 0)
      }

      const onChange = () => {
        const files = input.files ? Array.from(input.files) : []
        cleanup()
        resolve(files)
      }

      // If user cancels the picker, browsers usually don't fire 'change'.
      // Heuristic: when window regains focus and there's no 'change', treat as cancel.
      const onWindowFocus = () => {
        // Give a short grace period for 'change' to arrive first if it will.
        setTimeout(() => {
          if (!input.files || input.files.length === 0) {
            cleanup()
            resolve([])
          }
        }, 300)
      }

      input.addEventListener("change", onChange, { once: true })
      window.addEventListener("focus", onWindowFocus, true)
      document.body.appendChild(input)

      // Must be called from a user gesture (e.g., a button click) to avoid being blocked
      input.click()
    })
  }

  public static async fetchImageFromUrl(url: string): Promise<File> {
    const fullImageResult = await fetch(url)
    const arrayBuffer = await fullImageResult.arrayBuffer()
    const blob = new Blob([arrayBuffer], {
      type: fullImageResult.headers.get("content-type") || "image/*",
    })

    const file = new File([blob], "image.png", { type: blob.type })

    return file
  }
}
